// Keep this mapping aligned with Next.js i18n.domains. router.defaultLocale is
// the global default (en), including on the Turkish domain.
const PRODUCTION_ORIGIN = "https://www.digifist.com";
const TURKISH_ORIGIN = "https://www.digifist.com.tr";
const DEFAULT_LOCALES = ["en", "nl-be", "tr-tr"];
const LOCALE_SITES = {
  en: { origin: PRODUCTION_ORIGIN, prefix: "" },
  "nl-be": { origin: PRODUCTION_ORIGIN, prefix: "/nl-be" },
  "tr-tr": { origin: TURKISH_ORIGIN, prefix: "" },
};

function getSiteOrigin(value = process.env.NEXT_PUBLIC_SITE_URL) {
  try {
    const url = new URL(value || PRODUCTION_ORIGIN);
    if (["www.digifist.com.tr", "digifist.com.tr"].includes(url.hostname)) return TURKISH_ORIGIN;
  } catch (_) {}
  // A preview host, missing environment variable or typo must not become canonical.
  return PRODUCTION_ORIGIN;
}

function supportedLocale(locale, locales = DEFAULT_LOCALES) {
  if (typeof locale !== "string") return null;
  const normalized = locale.toLowerCase();
  return LOCALE_SITES[normalized] && locales.some((item) => item.toLowerCase() === normalized) ? normalized : null;
}

function languageTag(locale) {
  return locale.split("-").map((part, index) => index ? part.toUpperCase() : part).join("-");
}

function cleanPath(value = "/") {
  if (typeof value !== "string" || !value.startsWith("/") || value.startsWith("//")) return null;
  const path = value.split(/[?#]/)[0].replace(/\/+$/, "") || "/";
  return /[\[\]\\]|(?:^|\/)undefined(?:\/|$)|(?:^|\/)null(?:\/|$)/.test(path) ? null : path;
}

function stripLocale(path) {
  const segments = path.split("/");
  if (DEFAULT_LOCALES.includes(segments[1]?.toLowerCase())) segments.splice(1, 1);
  return segments.join("/") || "/";
}

function localizedPath(path, locale, locales = DEFAULT_LOCALES) {
  const language = supportedLocale(locale, locales);
  const clean = cleanPath(path);
  if (!language || !clean) return null;
  let barePath = stripLocale(clean);
  // This is the public Dutch route backed by the existing Next.js rewrite.
  if (language === "nl-be") barePath = barePath.replace(/^\/themes(?=\/|$)/, "/shopify-themas");
  else barePath = barePath.replace(/^\/shopify-themas(?=\/|$)/, "/themes");
  const prefix = LOCALE_SITES[language].prefix;
  return `${prefix}${barePath === "/" ? "" : barePath}` || "/";
}

function localizedUrl(path, locale, locales = DEFAULT_LOCALES) {
  const language = supportedLocale(locale, locales);
  const pathname = localizedPath(path, language, locales);
  return pathname ? new URL(pathname, LOCALE_SITES[language].origin).toString() : null;
}

function validHttpUrl(value) {
  try {
    const url = new URL(value);
    return ["https:", "http:"].includes(url.protocol) && !url.username && !url.password ? url : null;
  } catch (_) {
    return null;
  }
}

/** @param {{asPath?: string, locale?: string, locales?: readonly string[], explicit?: string | null}} [options] */
function canonicalUrl({ asPath = "/", locale = "en", locales = DEFAULT_LOCALES, explicit } = {}) {
  if (explicit) {
    const relative = explicit.startsWith("/") && !explicit.startsWith("//");
    const explicitPathLocale = relative ? supportedLocale(explicit.split("/")[1], locales) : null;
    const url = validHttpUrl(relative ? localizedUrl(explicit, explicitPathLocale || locale, locales) : explicit);
    if (url) {
      const knownDomain = ["digifist.com", "www.digifist.com", "digifist.vercel.app", "digifist.com.tr", "www.digifist.com.tr"].includes(url.hostname);
      if (knownDomain) {
        const explicitLocale = supportedLocale(url.pathname.split("/")[1], locales) ||
          (url.hostname.endsWith(".com.tr") ? "tr-tr" : "en");
        const normalized = localizedUrl(url.pathname, explicitLocale, locales);
        if (!normalized) return null;
        const canonical = new URL(normalized);
        canonical.search = url.search;
        return canonical.toString();
      }
      url.hash = "";
      return url.toString();
    }
  }
  const href = localizedUrl(asPath, locale, locales);
  if (!href) return null;
  const url = new URL(href);
  // Pagination changes blog content. Keep it self-canonical, without tracking parameters.
  const page = new URL(asPath, url.origin).searchParams.get("page");
  if (/^\/blog(?:\/tag\/[^/]+)?$/.test(stripLocale(url.pathname)) && page && /^[1-9]\d*$/.test(page) && Number(page) > 1) {
    url.searchParams.set("page", page);
  }
  return url.toString();
}

function isNoindexPath(path) {
  const clean = cleanPath(path);
  return clean ? /^(?:\/(?:search|404|500|thank-you)(?:\/|$))|\/(?:search|404|500)$/.test(stripLocale(clean)) : true;
}

function robotsContent(path, values = []) {
  const directives = [...new Set(values.filter((value) => typeof value === "string" && value !== "none")
    .flatMap((value) => value.split(",").map((directive) => directive.trim().toLowerCase()).filter(Boolean)))];
  if (isNoindexPath(path) && !directives.includes("noindex")) directives.push("noindex");
  return directives.filter((value) => !(value === "index" && directives.includes("noindex")) &&
    !(value === "follow" && directives.includes("nofollow"))).join(", ");
}

function alternateLinks(items, { asPath = "/", locales = DEFAULT_LOCALES } = {}) {
  const links = [];
  const seen = new Set();
  for (const item of items || []) {
    const locale = supportedLocale(item.locale, locales);
    const href = localizedUrl(item.path || asPath, locale, locales);
    if (!locale || !href || seen.has(locale)) continue;
    seen.add(locale);
    links.push({ hrefLang: languageTag(locale), href });
  }
  const fallback = links.find((item) => item.hrefLang === "en");
  if (fallback) links.push({ hrefLang: "x-default", href: fallback.href });
  return links;
}

function contentAlternates(data, { asPath = "/", pathname = "/", locales = DEFAULT_LOCALES } = {}) {
  const attributes = data?.attributes || data;
  if (!attributes || !supportedLocale(attributes.locale, locales)) return [];
  const clean = cleanPath(asPath);
  if (!clean) return [];
  const home = stripLocale(clean) === "/";
  const items = [{ locale: attributes.locale, path: clean }];
  for (const item of attributes.localizations?.data || []) {
    const translated = item.attributes || item;
    const slug = translated.slug_overwrite || translated.Slug || translated.slug;
    let path = home ? "/" : pathname;
    if (!home && pathname.includes("[")) {
      // Nested dynamic routes supply explicit localized paths via HreflangControlled.
      if (!slug || (pathname.match(/\[[^\]]+\]/g) || []).length !== 1) continue;
      path = pathname.replace(/\[[^\]]+\]/, slug);
    }
    items.push({ locale: translated.locale, path });
  }
  return alternateLinks(items, { asPath, locales });
}

function languageUrls(links) {
  return links.filter((item) => item.hrefLang !== "x-default").map((item) => ({
    locale: item.hrefLang.toLowerCase(),
    url: stripLocale(new URL(item.href).pathname),
  }));
}

function serializeJsonLd(value) {
  return JSON.stringify(value).replace(/</g, "\\u003c").replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026").replace(/\u2028/g, "\\u2028").replace(/\u2029/g, "\\u2029");
}

module.exports = { PRODUCTION_ORIGIN, TURKISH_ORIGIN, DEFAULT_LOCALES, LOCALE_SITES, getSiteOrigin, supportedLocale,
  cleanPath, stripLocale, localizedPath, localizedUrl, canonicalUrl, isNoindexPath, robotsContent,
  alternateLinks, contentAlternates, languageUrls, serializeJsonLd };
